import type { RedlineFailure } from "./redline-failure";
import { reportRedlineFailure } from "./redline-failure-report";
import type {
    RedlineApplyDropCount,
    RedlineApplySource,
    RedlineAttemptOutcome,
} from "./redline-apply-outcome";
import { describeApplySourceForWire, recordRedlineApplyOutcomes } from "./redline-apply-outcome";
import type { RedlineApplyContext } from "./redline-snapshot";

export interface RecordRedlineFailureArgs {
    applyContext: RedlineApplyContext;
    failure: RedlineFailure;
    error: Error;
}

export interface RedlineAttemptRecorder {
    recordFailureAndKeepItsSnapshotId(args: RecordRedlineFailureArgs): void;
    recordAttemptEndedAs(outcome: RedlineAttemptOutcome): void;
    whenEveryRecordingHasSettled(): Promise<void>;
}

function warnRecordingFailed(step: string, error: unknown): void {
    console.warn(`[redline-apply-recorder] ${step} failed`, error);
}

export function createRedlineAttemptRecorder(source: RedlineApplySource): RedlineAttemptRecorder {
    const snapshotIdsOfThisAttempt: string[] = [];
    let settledSoFar: Promise<void> = Promise.resolve();

    const recordBehindTheApply = (step: string, work: () => Promise<void>): void => {
        settledSoFar = settledSoFar.then(work).catch(error => warnRecordingFailed(step, error));
    };

    return {
        recordFailureAndKeepItsSnapshotId({ applyContext, failure, error }: RecordRedlineFailureArgs): void {
            recordBehindTheApply("snapshot upload", async () => {
                const report = await reportRedlineFailure({ applyContext, failure, error });
                if (report.snapshotId) {
                    snapshotIdsOfThisAttempt.push(report.snapshotId);
                }
            });
        },

        recordAttemptEndedAs(outcome: RedlineAttemptOutcome): void {
            recordBehindTheApply("outcome post", () => recordRedlineApplyOutcomes({
                ...describeApplySourceForWire(source),
                entries: [{ outcome: "attempted", count: 1 }, { outcome, count: 1 }],
                drops: [],
                snapshotIds: [...snapshotIdsOfThisAttempt],
            }));
        },

        whenEveryRecordingHasSettled(): Promise<void> {
            return settledSoFar;
        },
    };
}

export interface RecordRedlineGeneratedArgs {
    source: RedlineApplySource;
    generated: number;
}

export async function recordHowManyRedlinesWereGenerated({
    source,
    generated,
}: RecordRedlineGeneratedArgs): Promise<void> {
    try {
        await recordRedlineApplyOutcomes({
            ...describeApplySourceForWire(source),
            entries: [{ outcome: "generated", count: generated }],
            drops: [],
            snapshotIds: [],
        });
    } catch (error) {
        warnRecordingFailed("generated post", error);
    }
}

export interface RecordRedlineSelectionArgs {
    source: RedlineApplySource;
    selected: number;
    drops: RedlineApplyDropCount[];
}

export async function recordHowManyRedlinesTheGestureSelected({
    source,
    selected,
    drops,
}: RecordRedlineSelectionArgs): Promise<void> {
    const dropped = drops.reduce((total, drop) => total + drop.count, 0);
    const entries = [
        { outcome: "selected" as const, count: selected },
        ...(dropped > 0 ? [{ outcome: "dropped" as const, count: dropped }] : []),
    ];

    try {
        await recordRedlineApplyOutcomes({
            ...describeApplySourceForWire(source),
            entries,
            drops,
            snapshotIds: [],
        });
    } catch (error) {
        warnRecordingFailed("selection post", error);
    }
}
